import type { ReactNode } from 'react';
import { ImageOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/Skeleton';
import { PersonFace } from '@/components/work/WorkLockScreen';
import { useSignedUrl } from '@/features/workMode/hooks';
import type { WorkPerson } from '@/features/workMode/useLookups';

/** Face + name, optionally with a second line. */
export function WorkerChip({
  person,
  sub,
  size = 32,
  className,
}: {
  person: WorkPerson;
  sub?: string | null;
  size?: number;
  className?: string;
}) {
  return (
    <div className={cn('flex min-w-0 items-center gap-2.5', className)}>
      <PersonFace person={person} size={size} className="shrink-0" />
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-foreground">{person.name}</p>
        {sub && <p className="truncate text-xs text-muted-foreground">{sub}</p>}
      </div>
    </div>
  );
}

/** A photo stored in one of the Work Mode buckets, rendered via a signed URL. */
export function StoredImage({
  bucket,
  path,
  alt,
  className,
  onClick,
}: {
  bucket: 'work-instructions' | 'work-captures';
  path: string;
  alt: string;
  className?: string;
  onClick?: () => void;
}) {
  const { data: url, isLoading, isError } = useSignedUrl(bucket, path);
  if (isLoading) return <Skeleton className={cn('rounded-lg', className)} />;
  if (isError || !url) {
    return (
      <div className={cn('flex items-center justify-center rounded-lg bg-secondary text-muted-foreground', className)}>
        <ImageOff className="h-5 w-5" />
      </div>
    );
  }
  const img = <img src={url} alt={alt} className={cn('rounded-lg object-cover', className)} draggable={false} />;
  if (!onClick) return img;
  return (
    <button type="button" onClick={onClick} className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg">
      {img}
    </button>
  );
}

/** Tiny label/value pair for detail panels. */
export function Fact({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  );
}

/** Empty row for lists inside cards. */
export function QuietEmpty({ children }: { children: ReactNode }) {
  return <p className="rounded-xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">{children}</p>;
}
